import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FiUser, FiFile, FiCheckCircle, FiClock, FiLogOut, 
  FiEdit, FiUpload, FiInfo, FiHome 
} from 'react-icons/fi';
import './DashboardPMB.css';

const DashboardPMB = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const pmbUser = localStorage.getItem('pmb_user');
    const pmbToken = localStorage.getItem('pmb_token');

    if (!pmbUser || !pmbToken) {
      toast.error('Silakan login terlebih dahulu');
      navigate('/pmb/login');
      return;
    }

    try {
      const userData = JSON.parse(pmbUser);
      setUser(userData);
    } catch (error) {
      console.error('Error parsing user data:', error);
      navigate('/pmb/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('pmb_token');
    localStorage.removeItem('pmb_user');
    toast.success('Logout berhasil');
    navigate('/pmb/login');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Menunggu Verifikasi', color: '#f39c12', icon: <FiClock /> },
      approved: { label: 'Disetujui', color: '#27ae60', icon: <FiCheckCircle /> },
      rejected: { label: 'Ditolak', color: '#e74c3c', icon: <FiInfo /> },
      registered: { label: 'Terdaftar', color: '#3498db', icon: <FiCheckCircle /> }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <div className="status-badge" style={{ backgroundColor: config.color }}>
        {config.icon}
        <span>{config.label}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="dashboard-pmb-page">
        <div className="loading">Memuat...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-pmb-page">
      <nav className="dashboard-nav">
        <div className="nav-brand">
          <h2>PMB Dashboard</h2>
        </div>
        <div className="nav-actions">
          <Link to="/pmb" className="nav-link">
            <FiHome /> Beranda
          </Link>
          <button onClick={handleLogout} className="btn-logout">
            <FiLogOut /> Logout
          </button>
        </div>
      </nav>

      <div className="dashboard-container">
        <div className="dashboard-header">
          <div className="welcome-card">
            <div className="welcome-content">
              <h1>Selamat Datang, {user?.name}!</h1>
              <p>Kelola pendaftaran dan data Anda di sini</p>
            </div>
            {user?.status && getStatusBadge(user.status)}
          </div>
        </div>

        <div className="dashboard-content">
          <div className="info-cards">
            <div className="info-card">
              <div className="card-icon" style={{ backgroundColor: '#3498db' }}>
                <FiUser />
              </div>
              <div className="card-content">
                <h3>Data Pribadi</h3>
                <p className="card-value">{user?.name}</p>
                <p className="card-label">{user?.email}</p>
                <p className="card-label">{user?.phone || '-'}</p>
              </div>
            </div>

            <div className="info-card">
              <div className="card-icon" style={{ backgroundColor: '#9b59b6' }}>
                <FiFile />
              </div>
              <div className="card-content">
                <h3>Program Studi</h3>
                <p className="card-value">{user?.program_studi || 'Belum dipilih'}</p>
                <p className="card-label">Jalur: {user?.jalur_pendaftaran || 'Reguler'}</p>
              </div>
            </div>

            <div className="info-card">
              <div className="card-icon" style={{ backgroundColor: '#e67e22' }}>
                <FiCheckCircle />
              </div>
              <div className="card-content">
                <h3>Status Pendaftaran</h3>
                <p className="card-value">{user?.status || 'pending'}</p>
                <p className="card-label">
                  {user?.status === 'pending' && 'Menunggu verifikasi admin'}
                  {user?.status === 'approved' && 'Lanjutkan ke tahap selanjutnya'}
                  {user?.status === 'rejected' && 'Hubungi admin untuk info lebih lanjut'}
                  {user?.status === 'registered' && 'Proses pendaftaran selesai'}
                </p>
              </div>
            </div>
          </div>

          <div className="action-section">
            <h2>Langkah Selanjutnya</h2>
            
            {user?.status === 'pending' && (
              <div className="alert alert-info">
                <FiClock />
                <div>
                  <strong>Menunggu Verifikasi</strong>
                  <p>Akun Anda sedang dalam proses verifikasi oleh admin. Mohon tunggu atau hubungi admin untuk informasi lebih lanjut.</p>
                </div>
              </div>
            )}

            {user?.status === 'approved' && (
              <div className="action-cards">
                <div className="action-card">
                  <div className="action-icon">
                    <FiEdit />
                  </div>
                  <h3>Lengkapi Data</h3>
                  <p>Isi formulir pendaftaran lengkap untuk melanjutkan proses</p>
                  <button className="btn-action" disabled>
                    Formulir Pendaftaran
                    <span className="badge">Segera Hadir</span>
                  </button>
                </div>

                <div className="action-card">
                  <div className="action-icon">
                    <FiUpload />
                  </div>
                  <h3>Upload Dokumen</h3>
                  <p>Upload dokumen persyaratan pendaftaran</p>
                  <button className="btn-action" disabled>
                    Upload Dokumen
                    <span className="badge">Segera Hadir</span>
                  </button>
                </div>
              </div>
            )}

            {user?.status === 'rejected' && (
              <div className="alert alert-danger">
                <FiInfo />
                <div>
                  <strong>Pendaftaran Ditolak</strong>
                  <p>Maaf, pendaftaran Anda ditolak. Silakan hubungi admin untuk informasi lebih lanjut atau mendaftar ulang dengan data yang benar.</p>
                </div>
              </div>
            )}

            {user?.status === 'registered' && (
              <div className="alert alert-success">
                <FiCheckCircle />
                <div>
                  <strong>Pendaftaran Berhasil!</strong>
                  <p>Selamat! Anda telah terdaftar sebagai mahasiswa baru. Informasi selanjutnya akan dikirimkan melalui email.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPMB;
